<template>
  <div class="tree-node" :class="{ 'is-root': depth === 0 }">
    <!-- Task Card -->
    <div
      class="task-card"
      :class="{
        'is-current': task.isCurrentTask,
        'is-completed': task.workflowStatus?.isFinal,
        'depth-0': depth === 0,
        'depth-1': depth === 1,
        'depth-2plus': depth >= 2,
      }"
      :style="{ marginLeft: depth > 0 ? `${depth * 32}px` : '0' }"
    >
      <div class="card-header">
        <div class="header-left">
          <!-- Expand/collapse button -->
          <button
            v-if="task.children && task.children.length > 0"
            class="expand-btn"
            @click="expanded = !expanded"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="expand-icon"
              :class="{ expanded: expanded }"
            >
              <path d="m6 9 6 6 6-6"></path>
            </svg>
          </button>
          <div v-else class="expand-spacer"></div>

          <!-- Status Icon -->
          <svg
            v-if="task.workflowStatus?.isFinal"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="status-icon completed-icon"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="m9 12 2 2 4-4"></path>
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="status-icon in-progress-icon"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              :fill="task.workflowStatus?.color || '#2AABEE'"
              stroke="#fff"
              stroke-width="2"
            ></circle>
          </svg>

          <!-- Task Info -->
          <div class="task-info">
            <a
              :href="taskUrl(task.iid || task.id)"
              class="task-title"
              :class="{ completed: task.workflowStatus?.isFinal }"
            >
              {{ task.title }}
            </a>
            <div class="task-meta">
              <!-- Assignee -->
              <span v-if="task.assignee" class="meta-item assignee">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="meta-icon"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                {{ task.assignee.name || task.assignee.username }}
              </span>

              <!-- Due Date -->
              <span v-if="task.dueAt" class="meta-item due-date">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="meta-icon"
                >
                  <path d="M8 2v4"></path>
                  <path d="M16 2v4"></path>
                  <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                  <path d="M3 10h18"></path>
                </svg>
                {{ formatDueDate(task.dueAt) }}
              </span>

              <!-- Status Badge -->
              <span
                v-if="task.workflowStatus"
                class="meta-badge status-badge"
                :style="statusBadgeStyle"
              >
                {{ task.workflowStatus.name }}
              </span>

              <!-- Priority Icon -->
              <span
                v-if="task.priority != null && task.priority !== 0"
                class="priority-icon-wrapper"
              >
                <PriorityIcon :priority="task.priority" :size="16" />
              </span>
            </div>
          </div>
        </div>
        <div v-if="canShowUnlinkParent || canShowUnlinkChild" class="header-actions">
          <button
            v-if="canShowUnlinkParent"
            class="relation-action-btn"
            :disabled="unlinkingParent"
            :aria-label="s__('TaskHierarchy|Unlink Parent')"
            :title="s__('TaskHierarchy|Unlink Parent')"
            @click.stop="handleUnlinkParent"
          >
            <UnlinkIcon class="relation-action-icon" />
            <span class="relation-action-text">{{
              unlinkingParent
                ? s__('TaskHierarchy|Unlinking...')
                : s__('TaskHierarchy|Unlink Parent')
            }}</span>
          </button>
          <button
            v-if="canShowUnlinkChild"
            class="relation-action-btn"
            :disabled="isUnlinkingChild"
            :aria-label="s__('TaskHierarchy|Unlink Child')"
            :title="s__('TaskHierarchy|Unlink Child')"
            @click.stop="handleUnlinkChild"
          >
            <UnlinkIcon class="relation-action-icon" />
            <span class="relation-action-text">{{
              isUnlinkingChild
                ? s__('TaskHierarchy|Unlinking...')
                : s__('TaskHierarchy|Unlink Child')
            }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Children -->
    <div v-if="expanded && task.children && task.children.length > 0" class="tree-children">
      <TaskTreeNode
        v-for="child in task.children"
        :key="child.id"
        :task="child"
        :project-id="projectId"
        :chat-id="chatId"
        :from-id="fromId"
        :depth="depth + 1"
        :can-manage-relations="canManageRelations"
        :current-task-id="currentTaskId"
        :current-task-parent-id="currentTaskParentId"
        :current-task-child-ids="currentTaskChildIds"
        :unlinking-parent="unlinkingParent"
        :unlinking-child-id="unlinkingChildId"
        @unlink-parent="emit('unlink-parent')"
        @unlink-child="emit('unlink-child', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { getContrastTextColor } from '@/utils/colorContrast'
import { formatRelativeDate } from '@/utils/dateFormat'
import { s__ } from '~/locale'
import PriorityIcon from '@/components/common/icons/PriorityIcon.vue'
import UnlinkIcon from '@/components/common/icons/UnlinkIcon.vue'

const props = defineProps({
  task: { type: Object, required: true },
  projectId: { type: [String, Number], required: true },
  chatId: { type: [String, Number], default: null },
  fromId: { type: [String, Number], default: null },
  depth: { type: Number, default: 0 },
  canManageRelations: { type: Boolean, default: false },
  currentTaskId: { type: [String, Number], default: null },
  currentTaskParentId: { type: [String, Number], default: null },
  currentTaskChildIds: { type: Array, default: () => [] },
  unlinkingParent: { type: Boolean, default: false },
  unlinkingChildId: { type: [String, Number], default: null },
})
const emit = defineEmits(['unlink-parent', 'unlink-child'])

const expanded = ref(props.depth < 2) // Auto-expand first 2 levels
const normalizedTaskId = computed(() => String(props.task.id))
const isCurrentTask = computed(() => normalizedTaskId.value === String(props.currentTaskId))
const isDirectChildOfCurrent = computed(() => {
  return props.currentTaskChildIds.map(id => String(id)).includes(normalizedTaskId.value)
})
const canShowUnlinkParent = computed(() => {
  return props.canManageRelations && isCurrentTask.value && Boolean(props.currentTaskParentId)
})
const canShowUnlinkChild = computed(() => {
  return props.canManageRelations && isDirectChildOfCurrent.value
})
const isUnlinkingChild = computed(() => String(props.unlinkingChildId) === normalizedTaskId.value)

// Computed style for status badge with proper contrast
const statusBadgeStyle = computed(() => {
  if (!props.task.workflowStatus?.color) return {}

  const bgColor = props.task.workflowStatus.color
  const textColor = getContrastTextColor(bgColor)

  return {
    backgroundColor: bgColor,
    color: textColor,
  }
})

function taskUrl(taskId) {
  if (!taskId) return '#'
  let url = `/projects/${props.projectId}/tasks/${taskId}`
  const params = []
  if (props.chatId) params.push(`chat_id=${props.chatId}`)
  if (props.fromId) params.push(`from_id=${props.fromId}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
}

function formatDueDate(dueAt) {
  if (!dueAt) return ''
  // Use existing date formatting utility with proper internationalization
  return formatRelativeDate(dueAt)
}

function handleUnlinkParent() {
  emit('unlink-parent')
}

function handleUnlinkChild() {
  emit('unlink-child', props.task.id)
}
</script>

<style scoped>
.tree-node {
  margin-bottom: 8px;
}

.tree-node.is-root {
  margin-bottom: 12px;
}

.task-card {
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  transition: all 0.2s;
}

.task-card:hover {
  background: #f9fafb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.task-card.is-current {
  border-color: #2aabee;
  background: #e3f2fd;
}

.task-card.is-current:hover {
  background: #bbdefb;
}

.task-card.is-completed {
  opacity: 0.7;
}

.task-card.depth-0 {
  background: #f9fafb;
  border-color: #d1d5db;
}

.task-card.depth-1 {
  border-color: #e5e7eb;
}

.task-card.depth-2plus {
  border-color: #f3f4f6;
}

.card-header {
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.header-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.relation-action-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 11px;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.relation-action-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #374151;
}

.relation-action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.relation-action-icon {
  display: none;
  width: 18px;
  height: 18px;
}

@media (max-width: 640px) {
  .relation-action-btn {
    padding: 4px;
    gap: 0;
    min-width: 26px;
    min-height: 26px;
    border-radius: 5px;
  }

  .relation-action-text {
    display: none;
  }

  .relation-action-icon {
    display: inline-block;
  }
}

.expand-btn {
  padding: 2px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  flex-shrink: 0;
  border-radius: 4px;
  transition: all 0.2s;
}

.expand-btn:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.expand-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.2s;
  transform: rotate(-90deg);
}

.expand-icon.expanded {
  transform: rotate(0deg);
}

.expand-spacer {
  width: 20px;
  flex-shrink: 0;
}

.status-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.completed-icon {
  color: #10b981;
}

.in-progress-icon circle {
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.task-info {
  flex: 1;
  min-width: 0;
}

.task-title {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  text-decoration: none;
  display: block;
  margin-bottom: 6px;
  line-height: 1.4;
}

.task-title:hover {
  color: #2aabee;
}

.task-title.completed {
  text-decoration: line-through;
  color: #9ca3af;
}

.task-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #6b7280;
}

.meta-item.assignee {
  color: #6b7280;
}

.meta-item.due-date {
  color: #6b7280;
}

.meta-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.meta-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.priority-icon-wrapper {
  display: flex;
  align-items: center;
}

.tree-children {
  margin-top: 8px;
}

.is-completed .task-title {
  text-decoration: line-through;
  color: #9ca3af;
}

.is-completed .meta-item {
  color: #9ca3af;
}
</style>
